import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { TbExternalLink } from 'react-icons/tb'
import { Button } from 'antd'
import styled from '@emotion/styled'
import { keyframes, css } from '@emotion/react'
import {
  Message,
  DISMISSABLE_MESSAGES,
  MessageType
} from 'hooks/useGlobalContext/useMessageStore'

const FADE_DELAY = 350
const FADE_DURATION = 250
const ENTER_DURATION = FADE_DELAY + FADE_DURATION
const EXIT_DURATION = 300

const MessageBar = ({
  msg,
  onDismiss,
  active = true,
  animateHeight = false
}: {
  msg: Message
  onDismiss: (id: string) => void
  active?: boolean
  animateHeight?: boolean
}) => {
  const [state, setState] = useState<'entering' | 'visible' | 'exiting'>(
    'entering'
  )
  const isExitingRef = useRef(false)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!animateHeight) return
    const wrapper = wrapperRef.current
    if (wrapper) {
      wrapper.style.height = '0px'
      wrapper.offsetHeight // eslint-disable-line no-unused-expressions
      wrapper.style.height = `${wrapper.scrollHeight}px`
    }
  }, [])

  useEffect(() => {
    const enterTimer = setTimeout(() => {
      if (isExitingRef.current) return
      setState('visible')
      if (animateHeight) {
        const wrapper = wrapperRef.current
        if (wrapper) wrapper.style.height = ''
      }
    }, ENTER_DURATION)

    return () => clearTimeout(enterTimer)
  }, [])

  useEffect(() => {
    if (msg.type !== 'message' || !active) {
      return
    }

    const duration = (msg.duration ?? 3) * 1000
    exitTimerRef.current = setTimeout(() => triggerExit(), duration)

    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current)
      }
    }
  }, [msg.type, msg.duration])

  const triggerExit = () => {
    if (isExitingRef.current) {
      return
    }

    isExitingRef.current = true
    setState('exiting')

    const bar = barRef.current
    if (bar) {
      bar.style.transition = `opacity ${EXIT_DURATION}ms ease, transform ${EXIT_DURATION}ms ease`
      // Force reflow so the browser registers the transition property
      // before applying new values — without this, the change is batched
      // and the transition won't animate.
      bar.offsetHeight // eslint-disable-line no-unused-expressions
      bar.style.opacity = '0'
      bar.style.transform = 'translateX(100px)'
    }

    setTimeout(() => onDismiss(msg.id), EXIT_DURATION)
  }

  const handleDismiss = () => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current)
    }

    triggerExit()
  }

  return (
    <Wrapper ref={wrapperRef}>
      <Bar ref={barRef} entering={state === 'entering'} type={msg.type}>
        <div>
          <span>{msg.content}</span>
        </div>
        {active && (
          <Actions>
            {msg.action &&
              (msg.action.to ? (
                <Link to={msg.action.to}>
                  <ActionButton>
                    {msg.action.label}
                    <TbExternalLink style={{ fill: '#4a4a68' }} size='16' />
                  </ActionButton>
                </Link>
              ) : (
                <ActionButton onClick={msg.action.onClick}>
                  {msg.action.label}
                </ActionButton>
              ))}
            {(msg.dismissable || DISMISSABLE_MESSAGES.includes(msg.type)) && (
              <ActionButton onClick={handleDismiss} msgType={msg.type}>
                Dismiss
              </ActionButton>
            )}
          </Actions>
        )}
      </Bar>
    </Wrapper>
  )
}

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-58px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const Wrapper = styled.div`
  transition: height ${EXIT_DURATION}ms ease, margin-top ${EXIT_DURATION}ms ease;

  & + & {
    margin-top: 8px;
  }
`

const Bar = styled.div<{ entering: boolean; type: MessageType }>(
  ({ entering, type }) => {
    const backgroundColor = type === 'error' ? '#FFF0F0' : '#e9e8fc'
    const borderColor = type === 'error' ? '#A40000' : '#4a4a68'

    return css`
      width: 100%;
      padding: 12px 16px;

      background-color: ${backgroundColor};
      border: 1px solid ${borderColor};
      border-radius: 6px;

      color: ${borderColor};
      font-weight: 500;
      font-size: 13px;

      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;

      ${entering &&
      css`
        animation: ${fadeInDown} ${FADE_DURATION}ms ease ${FADE_DELAY}ms both;
      `}
    `
  }
)

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'msgType'
})<{ msgType?: MessageType }>(
  ({ msgType }) => `
  display: flex;
  align-items: center;
  gap: 8px;

  ${
    msgType === 'error' &&
    `
    border-color: #A40000;
    background-color: #A40000;
    color: #FFF;

    &:hover,
    &:focus,
    &:active {
        border-color: #A40000;
      background-color: #A40000;
      color: #FFF;
    }
  `
  }
`
)

export default MessageBar
